import React, { useState, useEffect, useCallback } from 'react';
import useIntersectionObserver from '../../hooks/useIntersectionObserver';

const GoogleLogo = ({ className = 'w-5 h-5' }) => (
  <svg className={className} viewBox="0 0 48 48" aria-label="Google">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

const StarRating = ({ rating }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((star) => (
      <svg
        key={star}
        className={`w-5 h-5 ${star <= Math.round(rating) ? 'text-yellow-400' : 'text-gray-500'}`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
    ))}
  </div>
);

// Opinie zapasowe — używane tylko gdy API Google nie odpowie
const FALLBACK_REVIEWS = [
  {
    author: 'Tomek Kowalski',
    rating: 5,
    date: 'miesiąc temu',
    text: 'Zdecydowanie najlepsza myjnia w Gdańsku, w dodatku z bardzo uczciwymi cenami. Właściciel bardzo sympatyczny i zna się na rzeczy. Auto perfekcyjnie przygotowane - polecam! 😃',
    color: 'bg-blue-500',
  },
  {
    author: 'Aleksandra Konarzewska',
    rating: 5,
    date: '3 miesiące temu',
    text: 'Bardzo polecam, samochód po czyszczeniu wnętrza wygląda jak z salonu, jeździ ze mną pies, a po odkurzaniu i praniu tapicerki nie ma śladu po sierści. Sprawny i miły kontakt, oraz korzystny pakiet 😊',
    color: 'bg-pink-500',
  },
  {
    author: 'Marcin Zwierz',
    rating: 5,
    date: '3 miesiące temu',
    text: 'Nie szukaj dalej już nie musisz. Znalazłeś myjnię w której jakość oraz terminowość stoją na najwyższym poziomie. Polecam z całego serca!',
    color: 'bg-green-600',
  },
  {
    author: 'Katarzyna Makarska',
    rating: 5,
    date: '7 miesięcy temu',
    text: 'Jestem bardzo zadowolona z czyszczenia auta. Tapicerka dobrze wybrana, środek wyczyszczony fenomenalnie. Pół psiego włoska nie zostało! Polecam serdecznie i na pewno skorzystam ponownie z usług.',
    color: 'bg-purple-500',
  },
  {
    author: 'Grzegorz Olszewski',
    rating: 5,
    date: '7 miesięcy temu',
    text: 'Dałem samochód do umycia i wyczyszczenia przed sprzedażą. Termin był praktycznie od razu, samochód pięknie i starannie wyczyszczony, wyprana tapicerka, wszystkie ślady wożenia małego rozbójnika zniknęły 😄 Polecam!',
    color: 'bg-teal-500',
  },
  {
    author: 'Dagmara Ścieszko',
    rating: 5,
    date: '7 miesięcy temu',
    text: 'Polecam czyszczenie auta w tym miejscu. Szybko i profesjonalnie. Miła obsługa. Korzystałam już kilka razy i za każdym razem 100% zadowolenia. Na pewno wrócę!',
    color: 'bg-orange-500',
  },
  {
    author: 'Agata Nowak',
    rating: 5,
    date: '5 miesięcy temu',
    text: 'Jestem zachwycona efektem! Samochód lśni, zarówno wewnątrz, jak i na zewnątrz. Dokładna, bardzo solidna robota! Na pewno skorzystam ponownie.',
    color: 'bg-indigo-500',
  },
  {
    author: 'Dawid Popiela',
    rating: 5,
    date: '8 miesięcy temu',
    text: 'Mogę polecić. Moja pierwsza wizyta u detailera. Autko w środku sumiennie wysprzątane. Fotele po wypraniu wyglądają jak nowe. Ceny patrząc po Gdańsku OK. Myślę że jeszcze skorzystam.',
    color: 'bg-red-500',
  },
  {
    author: 'Karol Dobosz',
    rating: 5,
    date: '6 miesięcy temu',
    text: 'Myjnia ręczna wykonała świetną i profesjonalną robotę - auto jest czyste i dopracowane w każdym detalu. Jestem bardzo zadowolony z usługi i z przyjemnością skorzystam ponownie. Polecam!',
    color: 'bg-yellow-600',
  },
  {
    author: 'Marek Nozka',
    rating: 5,
    date: 'rok temu',
    text: 'Byłem po raz pierwszy. Zamówiłem Pakiet Plus. Jestem bardzo zadowolony z usługi. Pełen profesjonalizm obsługi. Na pewno przyjadę ponownie. Ceny bardzo przystępne w porównaniu z innymi. Bardzo polecam.',
    color: 'bg-cyan-600',
  },
];

const VISIBLE = 3;
const COLORS = ['bg-blue-500', 'bg-pink-500', 'bg-green-600', 'bg-purple-500', 'bg-teal-500', 'bg-orange-500', 'bg-indigo-500', 'bg-red-500'];

const mapApiReviews = (reviews) =>
  reviews
    .filter((r) => r.text && r.text.trim())
    .map((r, i) => ({
      author: r.author_name || 'Klient Google',
      rating: r.rating || 5,
      date: r.relative_time_description || '',
      text: r.text.trim(),
      color: COLORS[i % COLORS.length],
    }));

const ReviewCard = ({ review, index, isVisible }) => (
  <div
    className={`
      bg-black/30 backdrop-blur-sm p-6 rounded-2xl border border-white/10
      transform transition-all duration-500 hover:-translate-y-1 hover:bg-black/40
      ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
    `}
    style={{ transitionDelay: `${index * 100}ms` }}
  >
    <div className="flex items-center gap-3 mb-4">
      <div className={`w-11 h-11 rounded-full ${review.color} text-white flex items-center justify-center font-bold text-lg uppercase flex-shrink-0`}>
        {review.author.charAt(0)}
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold text-white truncate">{review.author}</h3>
        <p className="text-sm text-gray-400">{review.date}</p>
      </div>
      <div className="ml-auto flex-shrink-0">
        <GoogleLogo />
      </div>
    </div>
    <div className="mb-3">
      <StarRating rating={review.rating} />
    </div>
    <p className="text-gray-300 italic leading-relaxed text-sm">
      "{review.text}"
    </p>
  </div>
);

const GoogleReviews = () => {
  const [startIndex, setStartIndex] = useState(0);
  const [titleRef, isTitleVisible] = useIntersectionObserver();
  const [isAnimating, setIsAnimating] = useState(false);
  const [reviews, setReviews] = useState(FALLBACK_REVIEWS);
  const [stats, setStats] = useState(null);
  const total = reviews.length;

  useEffect(() => {
    let cancelled = false;
    fetch('/api/google-reviews')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled && data && data.rating && data.user_ratings_total) {
          setStats({ rating: data.rating, count: data.user_ratings_total });
        }
        if (!cancelled && data && Array.isArray(data.reviews)) {
          const live = mapApiReviews(data.reviews);
          if (live.length >= VISIBLE) {
            setReviews(live);
            setStartIndex(0);
          }
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const next = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setTimeout(() => {
      setStartIndex((prev) => (prev + 1) % total);
      setIsAnimating(false);
    }, 250);
  }, [isAnimating, total]);

  const prev = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setTimeout(() => {
      setStartIndex((prev) => (prev - 1 + total) % total);
      setIsAnimating(false);
    }, 250);
  }, [isAnimating, total]);

  useEffect(() => {
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, [next]);

  const visible = Array.from({ length: VISIBLE }, (_, i) => reviews[(startIndex + i) % total]);

  return (
    <section className="py-20">
      <div className="container mx-auto px-4">
        {/* Nagłówek */}
        <div
          ref={titleRef}
          className={`text-center max-w-2xl mx-auto mb-14 transform transition-all duration-700 ease-out
            ${isTitleVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}
        >
          <div className="flex justify-center items-center gap-3 mb-3">
            <GoogleLogo className="w-8 h-8" />
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              Co mówią o nas klienci
            </h2>
          </div>
          {stats && (
            <div className="flex justify-center items-center gap-2 mt-3">
              <span className="text-2xl font-bold text-white">
                {stats.rating.toFixed(1).replace('.', ',')}
              </span>
              <StarRating rating={stats.rating} />
              <span className="text-gray-400 text-sm">({stats.count} opinii)</span>
            </div>
          )}
        </div>

        {/* Siatka opinii */}
        <div
          className={`grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto transition-opacity duration-250 ${isAnimating ? 'opacity-0' : 'opacity-100'}`}
        >
          {visible.map((review, index) => (
            <ReviewCard
              key={`${startIndex}-${index}`}
              review={review}
              index={index}
              isVisible={isTitleVisible}
            />
          ))}
        </div>

        {/* Kontrolki */}
        <div className="flex justify-center items-center gap-4 mt-10">
          <button
            onClick={prev}
            className="w-9 h-9 rounded-full bg-black/30 backdrop-blur-sm border border-white/10 flex items-center justify-center hover:bg-black/50 transition-all duration-200"
            aria-label="Poprzednie"
          >
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="flex gap-1.5">
            {reviews.map((_, i) => (
              <button
                key={i}
                onClick={() => setStartIndex(i)}
                className={`h-1.5 rounded-full transition-all duration-300 ${i === startIndex ? 'bg-primary-400 w-6' : 'bg-white/30 w-1.5'}`}
                aria-label={`Opinia ${i + 1}`}
              />
            ))}
          </div>
          <button
            onClick={next}
            className="w-9 h-9 rounded-full bg-black/30 backdrop-blur-sm border border-white/10 flex items-center justify-center hover:bg-black/50 transition-all duration-200"
            aria-label="Następne"
          >
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* CTA */}
        <div className="mt-10 text-center">
          <a
            href="https://www.google.com/maps/place/Auto+Detailing+Gda%C5%84sk+%7C+Naprawa+Tapicerki+Samochodowej+%7C+Renowacja+Sk%C3%B3r+%7C+R%C4%99czna+Myjnia+Samochodowa+%7C+Pranie+Tapicerki/@54.3612172,18.5832242,17z/data=!4m8!3m7!1s0x46fd75392fa99b11:0xe52a19a93eb935a7!8m2!3d54.3612172!4d18.5832242!9m1!1b1!16s%2Fg%2F11p56k063q?entry=ttu&g_ep=EgoyMDI2MDQwNi4wIKXMDSoASAFQAw%3D%3D"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-black/30 backdrop-blur-sm border border-white/10 text-white font-semibold py-3 px-6 rounded-lg hover:bg-black/50 transition-all duration-300"
          >
            <GoogleLogo className="w-5 h-5" />
            <span>Zobacz wszystkie opinie w Google</span>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
};

export default GoogleReviews;
